import { gql } from "@apollo/client";
import { formatNumeral, partition } from "@asmbl/shared/utils";
import {
  CashCompType,
  CondensedTable_matrix,
  MatrixTypeEnum,
  CondensedTableBonusGuidanceCell_participant as Participant,
} from "src/__generated__/graphql";
import { getBonusGuidance } from "src/models/Guidance";
import { AssembleTruncatedTypography } from "../../../../components/AssembleTruncatedTypography";
import { ColumnComponent2 } from "../CondensedTable/CondensedTableInner";
import { ColumnIds, ColumnIdsToHeaders } from "../Contexts/ColumnOrderContext";
import { useTableData } from "../Contexts/TableDataContext2";
import { DefaultCell } from "./DefaultCell";

type Props = {
  row: { original: Participant };
};

export function CondensedTableBonusGuidanceCell({
  row: { original: employee },
}: Props): JSX.Element {
  const { matrices, compCycle } = useTableData();

  const [_, allOtherMatrices] = partition(
    matrices,
    (matrix) => matrix.type === MatrixTypeEnum.MERIT
  );

  const bonusMatricesMap = new Map<MatrixTypeEnum, CondensedTable_matrix[]>();
  allOtherMatrices.forEach((matrix) => {
    if (matrix.type !== MatrixTypeEnum.BUDGET) {
      if (bonusMatricesMap.has(matrix.type)) {
        bonusMatricesMap.get(matrix.type)?.push(matrix);
      } else {
        bonusMatricesMap.set(matrix.type, [matrix]);
      }
    }
  });

  const bonusGuidance = getBonusGuidance(
    employee,
    bonusMatricesMap,
    Number(compCycle?.organizationCompCyclePerfRating)
  );

  const target = employee.subject.activeCashCompensation?.find(
    (comp) => comp.type === CashCompType.RECURRING_BONUS
  )?.annualCashEquivalent.value;

  const individualValue =
    "pay" in bonusGuidance.BONUS_GUIDANCE_INDIVIDUAL_PERFORMANCE
      ? bonusGuidance.BONUS_GUIDANCE_INDIVIDUAL_PERFORMANCE.pay
      : 0;

  const companyValue =
    "pay" in bonusGuidance.BONUS_GUIDANCE_COMPANY_PERFORMANCE
      ? bonusGuidance.BONUS_GUIDANCE_COMPANY_PERFORMANCE.pay
      : 0;

  const customValue =
    "pay" in bonusGuidance.BONUS_GUIDANCE_CUSTOM_PERFORMANCE
      ? bonusGuidance.BONUS_GUIDANCE_CUSTOM_PERFORMANCE.pay
      : 0;

  const guidance =
    target != null
      ? (individualValue + companyValue + customValue) / target
      : null;

  return (
    <div>
      {guidance != null ? (
        <AssembleTruncatedTypography align="right">
          {formatNumeral(guidance, {
            style: "percent",
            maximumFractionDigits: 2,
          })}
        </AssembleTruncatedTypography>
      ) : (
        <DefaultCell />
      )}
    </div>
  );
}

CondensedTableBonusGuidanceCell.fragments = {
  participant: gql`
    fragment CondensedTableBonusGuidanceCell_participant on CompCycleParticipant {
      subject {
        activeCashCompensation(currencyCode: $currencyCode) {
          type
          annualCashEquivalent
          hourlyCashEquivalent
          unit
          employeeId
        }
      }
      bonusIndividualPerfRating
      bonusCustomPerfRating
      subjectId
    }
  `,
};

CondensedTableBonusGuidanceCell.Header = ColumnIdsToHeaders.get(
  ColumnIds.BONUS_GUIDANCE
);
CondensedTableBonusGuidanceCell.id = ColumnIds.BONUS_GUIDANCE;

const column: ColumnComponent2["column"] = {
  Cell: CondensedTableBonusGuidanceCell,
  Header: CondensedTableBonusGuidanceCell.Header,
  id: CondensedTableBonusGuidanceCell.id,
  defaultCanSort: false,
};

CondensedTableBonusGuidanceCell.column = column;
